// Generic per-kind media tab of the F-F media panel: a language-filtered grid
// of supporting attachments of one `kind` (audio, video, pdf, or image) plus an
// upload control. Audio/video render an inline player; pdf renders an "Open PDF"
// link (F-F.4); image renders a thumbnail. Upload is one click (file only);
// caption + language are edited per-card AFTER upload (F-F.3 feedback) via the
// PATCH endpoint.
//
// Presentational + controlled: the container owns the upload/update/remove
// mutations.

import { useState, type ComponentType } from "react";
import {
  ArrowDown,
  ArrowUp,
  FileText,
  Film,
  Image as ImageIcon,
  Music,
  Trash2,
  Upload,
} from "lucide-react";

import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import type { AssetKind, MediaItem } from "@shared/api/exhibits";
import { filterMediaByLanguage } from "@shared/lib/media";

import FileButton from "./FileButton";

const FILTERS = ["all", "en", "tc", "sc"] as const;
type Filter = (typeof FILTERS)[number];

const FILTER_LABEL: Record<Filter, string> = {
  all: "All",
  en: "EN",
  tc: "TC",
  sc: "SC",
};

type MediaKind = "audio" | "video" | "pdf" | "image";

interface KindConfig {
  accept: string;
  icon: ComponentType<{ className?: string }>;
  noun: string;
}

const KIND_CONFIG: Record<MediaKind, KindConfig> = {
  audio: {
    accept: "audio/mpeg,audio/mp4,audio/x-wav,audio/wav",
    icon: Music,
    noun: "audio",
  },
  video: { accept: "video/mp4", icon: Film, noun: "video" },
  pdf: { accept: "application/pdf", icon: FileText, noun: "document" },
  image: {
    accept: "image/jpeg,image/png,image/webp",
    icon: ImageIcon,
    noun: "photo",
  },
};

export interface MediaKindTabProps {
  kind: MediaKind;
  media: MediaItem[];
  assetUrl: (assetId: string) => string;
  disabled?: boolean;
  uploading?: boolean;
  onUpload: (params: {
    file: File;
    kind: AssetKind;
    language: string | null;
    caption: string | null;
  }) => void;
  onUpdate: (
    assetId: string,
    patch: { caption: string | null; language: string | null },
  ) => void;
  onRemove: (assetId: string) => void;
  // Full asset-id sequence across ALL kinds, in the desired order — the
  // reorder endpoint replaces media[] wholesale.
  onReorder: (assetIds: string[]) => void;
}

// One card with local caption state. Caption saves on blur (when changed);
// language saves immediately on select. Both send the item's full
// {caption, language} so the PATCH replaces them wholesale.
interface MediaCardProps {
  kind: MediaKind;
  item: MediaItem;
  assetUrl: (assetId: string) => string;
  disabled?: boolean;
  onUpdate: MediaKindTabProps["onUpdate"];
  onRemove: (assetId: string) => void;
  onMove: (assetId: string, direction: -1 | 1) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const MediaCard = ({
  kind,
  item,
  assetUrl,
  disabled,
  onUpdate,
  onRemove,
  onMove,
  canMoveUp,
  canMoveDown,
}: MediaCardProps) => {
  const [caption, setCaption] = useState(item.caption ?? "");
  const [lang, setLang] = useState(item.language ?? "all");

  const save = (nextCaption: string, nextLang: string) =>
    onUpdate(item.assetId, {
      caption: nextCaption.trim() || null,
      language: nextLang === "all" ? null : nextLang,
    });

  const src = assetUrl(item.assetId);

  return (
    <div
      className="p-3 bg-white border border-border rounded-lg space-y-2"
      data-testid={`${kind}-card-${item.assetId}`}
    >
      {kind === "image" ? (
        <img
          src={src}
          alt={item.caption || "Image"}
          className="w-full max-h-48 object-cover rounded"
          data-testid={`${kind}-preview-${item.assetId}`}
        />
      ) : kind === "pdf" ? (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 rounded border border-border bg-muted/50 text-sm font-medium hover:bg-muted"
          data-testid={`${kind}-open-${item.assetId}`}
        >
          <FileText className="w-4 h-4 shrink-0" />
          <span className="truncate">{item.caption || "Open PDF"}</span>
        </a>
      ) : kind === "video" ? (
        <video
          controls
          preload="metadata"
          src={src}
          className="w-full rounded"
          data-testid={`${kind}-player-${item.assetId}`}
        />
      ) : (
        <audio
          controls
          preload="none"
          src={src}
          className="w-full"
          data-testid={`${kind}-player-${item.assetId}`}
        />
      )}

      <Input
        value={caption}
        placeholder="Caption (optional)"
        disabled={disabled}
        onChange={(e) => setCaption(e.target.value)}
        onBlur={() => {
          if ((caption.trim() || null) !== (item.caption ?? null)) {
            save(caption, lang);
          }
        }}
        data-testid={`${kind}-caption-${item.assetId}`}
      />

      <div className="flex items-center justify-between gap-2">
        <select
          value={lang}
          disabled={disabled}
          onChange={(e) => {
            setLang(e.target.value);
            save(caption, e.target.value);
          }}
          className="border rounded px-2 py-1 text-sm"
          data-testid={`${kind}-language-${item.assetId}`}
        >
          <option value="all">All languages</option>
          <option value="en">EN</option>
          <option value="tc">TC</option>
          <option value="sc">SC</option>
        </select>
        <div className="flex items-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || !canMoveUp}
            onClick={() => onMove(item.assetId, -1)}
            aria-label="Move up"
            data-testid={`${kind}-move-up-${item.assetId}`}
          >
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || !canMoveDown}
            onClick={() => onMove(item.assetId, 1)}
            aria-label="Move down"
            data-testid={`${kind}-move-down-${item.assetId}`}
          >
            <ArrowDown className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onRemove(item.assetId)}
            data-testid={`${kind}-remove-${item.assetId}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};

const MediaKindTab = ({
  kind,
  media,
  assetUrl,
  disabled,
  uploading,
  onUpload,
  onUpdate,
  onRemove,
  onReorder,
}: MediaKindTabProps) => {
  const [filter, setFilter] = useState<Filter>("all");

  const cfg = KIND_CONFIG[kind];
  const items = media.filter((m) => m.kind === kind);
  const shown = filterMediaByLanguage(items, filter);
  const busy = disabled || uploading;

  // Swap the item with its same-kind neighbor inside the FULL media order, so
  // items of other kinds keep their positions (the endpoint replaces media[]
  // wholesale; visitor pages render in array order).
  const moveItem = (assetId: string, direction: -1 | 1) => {
    const kindIds = items.map((i) => i.assetId);
    const pos = kindIds.indexOf(assetId);
    const neighbor = kindIds[pos + direction];
    if (neighbor === undefined) return;
    const full = media.map((m) => m.assetId);
    const a = full.indexOf(assetId);
    const b = full.indexOf(neighbor);
    [full[a], full[b]] = [full[b], full[a]];
    onReorder(full);
  };

  return (
    <div className="space-y-4" data-testid={`${kind}-tab`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="flex items-center gap-1"
          data-testid={`${kind}-language-filter`}
        >
          {FILTERS.map((f) => (
            <Button
              key={f}
              type="button"
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              data-testid={`${kind}-filter-${f}`}
            >
              {FILTER_LABEL[f]}
            </Button>
          ))}
        </div>
        <FileButton
          accept={cfg.accept}
          onFile={(file) =>
            onUpload({
              file,
              kind,
              language: filter === "all" ? null : filter,
              caption: null,
            })
          }
          disabled={busy}
          testId={`${kind}-upload`}
          variant="default"
        >
          <Upload className="w-4 h-4 mr-1" />
          {uploading ? "Uploading…" : `Upload ${cfg.noun}`}
        </FileButton>
      </div>

      {shown.length === 0 ? (
        <p
          className="text-sm text-muted-foreground py-6 text-center"
          data-testid={`${kind}-empty`}
        >
          No {cfg.noun} attachments yet. Upload one, then add a caption and
          language tag below it.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {shown.map((item) => {
            const pos = items.findIndex((i) => i.assetId === item.assetId);
            return (
              <MediaCard
                key={item.assetId}
                kind={kind}
                item={item}
                assetUrl={assetUrl}
                disabled={disabled}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onMove={moveItem}
                canMoveUp={pos > 0}
                canMoveDown={pos < items.length - 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaKindTab;
